import { useState, useEffect, useMemo } from "react";
import {
  parseAuthors,
  cleanAuthorName,
  getGoogleScholarAuthorUrl,
  getGoogleScholarAuthorProfileUrl,
  getGoogleScholarJournalAuthorUrl,
  getGoogleScholarArticleUrl,
  generateBibTeX,
  generateHighwireTags,
} from "./scholarUtils";

export default function AuthorIndexPage({ navigate, targetAuthor = null, fetchArticlesFn }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(targetAuthor ? cleanAuthorName(targetAuthor) : "");
  const [selectedLetter, setSelectedLetter] = useState("ALL");
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [expandedAuthors, setExpandedAuthors] = useState(() => {
    return targetAuthor ? { [cleanAuthorName(targetAuthor)]: true } : {};
  });
  const [toastMsg, setToastMsg] = useState("");
  const [scholarModalData, setScholarModalData] = useState(null);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 3500);
  };

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    fetchArticlesFn()
      .then((data) => {
        if (isMounted) {
          setArticles(data || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [fetchArticlesFn]);

  // Aggregate authors across all published articles
  const authorsData = useMemo(() => {
    const map = new Map();

    articles.forEach((art) => {
      const parsed = parseAuthors(art.authors);
      parsed.forEach((authorName, index) => {
        const key = authorName.toLowerCase().trim();
        if (!map.has(key)) {
          map.set(key, {
            name: authorName,
            articles: [],
            affiliations: new Set(),
            categories: new Set(),
            firstLetter: authorName.trim()[0]?.toUpperCase() || "#",
          });
        }
        const item = map.get(key);
        item.articles.push({
          ...art,
          authorRole: index === 0 ? "First / Lead Author" : "Co-Author",
        });
        if (art.affiliations) {
          item.affiliations.add(art.affiliations);
        }
        if (art.category) {
          item.categories.add(art.category);
        }
      });
    });

    const list = Array.from(map.values()).map((author) => ({
      ...author,
      affiliationsList: Array.from(author.affiliations),
      categoriesList: Array.from(author.categories),
      publicationCount: author.articles.length,
    }));

    // Sort alphabetically by last name if discernible, or by name
    list.sort((a, b) => {
      const getSortKey = (name) => {
        const parts = name.trim().split(/\s+/);
        return parts.length > 1 ? parts[parts.length - 1] : parts[0];
      };
      return getSortKey(a.name).localeCompare(getSortKey(b.name));
    });

    return list;
  }, [articles]);

  // Letters available
  const letters = useMemo(() => {
    const set = new Set();
    authorsData.forEach((a) => set.add(a.firstLetter));
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    return [
      "ALL",
      ...alphabet.filter((l) => set.has(l)),
    ];
  }, [authorsData]);

  // Filtered authors
  const filteredAuthors = useMemo(() => {
    return authorsData.filter((a) => {
      if (selectedLetter !== "ALL" && a.firstLetter !== selectedLetter) {
        return false;
      }
      if (selectedCategory !== "ALL" && !a.categoriesList.includes(selectedCategory)) {
        return false;
      }
      if (search.trim()) {
        const q = search.toLowerCase().trim();
        const matchesName = a.name.toLowerCase().includes(q);
        const matchesAffil = a.affiliationsList.some((aff) => aff.toLowerCase().includes(q));
        const matchesPaper = a.articles.some((p) => p.title.toLowerCase().includes(q));
        if (!matchesName && !matchesAffil && !matchesPaper) return false;
      }
      return true;
    });
  }, [authorsData, selectedLetter, selectedCategory, search]);

  const toggleExpand = (authorName) => {
    setExpandedAuthors((prev) => ({
      ...prev,
      [authorName]: !prev[authorName],
    }));
  };

  const copyAuthorBibtex = (author) => {
    const bib = author.articles.map((art) => generateBibTeX(art)).join("\n\n");
    navigator.clipboard.writeText(bib).then(() => {
      showToast(`Copied BibTeX for ${author.name} (${author.articles.length} publications)`);
    });
  };

  return (
    <div style={{ padding: "48px 0 80px", minHeight: "85vh" }}>
      <div className="container">
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
          <button
            onClick={() => navigate("home")}
            style={{
              fontFamily: "var(--mono)",
              fontSize: "11px",
              color: "var(--ink-40)",
              background: "none",
              border: "none",
              cursor: "pointer",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              padding: 0,
            }}
          >
            Front Page
          </button>
          <span style={{ color: "var(--ink-15)" }}>›</span>
          <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--scarlet)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Author Index & Google Scholar Directory
          </span>
        </div>

        {/* Page Header */}
        <div style={{ marginBottom: "36px", borderBottom: "1px solid var(--ink-15)", paddingBottom: "28px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <span
              style={{
                fontFamily: "var(--mono)",
                fontSize: "10px",
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                background: "rgba(192, 57, 43, 0.08)",
                color: "var(--scarlet)",
                padding: "4px 10px",
                borderRadius: "2px",
                border: "1px solid rgba(192, 57, 43, 0.2)",
              }}
            >
              Academic Indexing
            </span>
            <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--ink-40)" }}>
              Highwire Press & Dublin Core Compliant
            </span>
          </div>
          <h1
            className="display-lg"
            style={{
              fontFamily: "var(--serif-display)",
              fontSize: "36px",
              fontWeight: 900,
              color: "var(--ink)",
              marginBottom: "12px",
              letterSpacing: "-0.02em",
            }}
          >
            Author Index
          </h1>
          <p
            style={{
              fontSize: "17px",
              lineHeight: 1.6,
              color: "var(--ink-70)",
              maxWidth: "760px",
              fontWeight: 300,
            }}
          >
            Official directory of researchers, scholars, and contributors published in ScienceXplorer.
            Every author is automatically indexed with machine-readable citation tags for Google Scholar,
            Crossref DOI linkage, and open-access metadata harvesters.
          </p>
        </div>

        {/* Index Metrics Bar */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px",
            marginBottom: "36px",
          }}
        >
          <div style={{ background: "white", border: "1px solid var(--ink-15)", padding: "20px 24px" }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-40)", marginBottom: "6px" }}>
              Total Authors
            </div>
            <div style={{ fontFamily: "var(--serif-display)", fontSize: "28px", fontWeight: 700, color: "var(--ink)" }}>
              {authorsData.length}
            </div>
            <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)", marginTop: "4px" }}>
              Across all disciplines
            </div>
          </div>

          <div style={{ background: "white", border: "1px solid var(--ink-15)", padding: "20px 24px" }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-40)", marginBottom: "6px" }}>
              Indexed Papers
            </div>
            <div style={{ fontFamily: "var(--serif-display)", fontSize: "28px", fontWeight: 700, color: "var(--ink)" }}>
              {articles.length}
            </div>
            <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)", marginTop: "4px" }}>
              Peer-reviewed publications
            </div>
          </div>

          <div style={{ background: "white", border: "1px solid var(--ink-15)", padding: "20px 24px" }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-40)", marginBottom: "6px" }}>
              Scholar Tag Compliance
            </div>
            <div style={{ fontFamily: "var(--serif-display)", fontSize: "28px", fontWeight: 700, color: "#16A34A" }}>
              100%
            </div>
            <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)", marginTop: "4px" }}>
              Highwire Press & Dublin Core
            </div>
          </div>

          <div style={{ background: "white", border: "1px solid var(--ink-15)", padding: "20px 24px" }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-40)", marginBottom: "6px" }}>
              Access License
            </div>
            <div style={{ fontFamily: "var(--serif-display)", fontSize: "28px", fontWeight: 700, color: "var(--ink)" }}>
              CC-BY 4.0
            </div>
            <div style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)", marginTop: "4px" }}>
              Open-Access Crawler Permitted
            </div>
          </div>
        </div>

        {/* Filter & Search Controls */}
        <div
          style={{
            background: "white",
            border: "1px solid var(--ink-15)",
            padding: "20px 24px",
            marginBottom: "28px",
          }}
        >
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "center", marginBottom: "18px" }}>
            <div style={{ flex: 1, minWidth: "260px", position: "relative" }}>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search authors, institutions, or paper titles…"
                style={{
                  width: "100%",
                  padding: "10px 14px 10px 36px",
                  border: "1px solid var(--ink-15)",
                  fontFamily: "var(--serif-body)",
                  fontSize: "14px",
                  color: "var(--ink)",
                  outline: "none",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--ink-40)",
                  fontSize: "14px",
                }}
              >
                🔍
              </span>
              {search && (
                <button
                  onClick={() => setSearch("")}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--ink-40)",
                    fontSize: "16px",
                  }}
                >
                  ×
                </button>
              )}
            </div>

            <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
              {search.trim() && (
                <a
                  href={`https://scholar.google.com/scholar?q=author:"${encodeURIComponent(search.trim())}"`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-sm"
                  style={{
                    fontFamily: "var(--mono)",
                    fontSize: "10px",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    borderColor: "var(--scarlet)",
                    color: "var(--scarlet)",
                  }}
                >
                  🎓 Search "{search.trim()}" on Google Scholar ↗
                </a>
              )}
              {search.trim() && (
                <button
                  onClick={() => {
                    setSearch("");
                    setSelectedLetter("ALL");
                    setSelectedCategory("ALL");
                  }}
                  className="btn btn-ghost btn-sm"
                  style={{ fontFamily: "var(--mono)", fontSize: "10px" }}
                >
                  Reset Filters
                </button>
              )}
            </div>
          </div>

          {/* Letter Selector Tabs */}
          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)", marginRight: "6px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              Filter by Letter:
            </span>
            {letters.map((l) => {
              const active = selectedLetter === l;
              return (
                <button
                  key={l}
                  onClick={() => setSelectedLetter(l)}
                  style={{
                    padding: "4px 9px",
                    border: `1px solid ${active ? "var(--ink)" : "var(--ink-15)"}`,
                    background: active ? "var(--ink)" : "white",
                    color: active ? "white" : "var(--ink)",
                    fontFamily: "var(--mono)",
                    fontSize: "11px",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    minWidth: "28px",
                    textAlign: "center",
                  }}
                >
                  {l}
                </button>
              );
            })}
          </div>
        </div>

        {/* Results Info */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--ink-40)", letterSpacing: "0.05em" }}>
            Showing {filteredAuthors.length} author{filteredAuthors.length !== 1 ? "s" : ""}
            {selectedLetter !== "ALL" && ` starting with "${selectedLetter}"`}
            {search && ` matching "${search}"`}
          </span>
          <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--ink-40)" }}>
            Click an author or paper to explore citation details
          </span>
        </div>

        {/* Loading / Empty / Authors List */}
        {loading ? (
          <div style={{ padding: "60px 0", textAlign: "center", fontFamily: "var(--mono)", fontSize: "12px", color: "var(--ink-40)" }}>
            Loading Academic Author Index…
          </div>
        ) : filteredAuthors.length === 0 ? (
          <div style={{ background: "white", border: "1px solid var(--ink-15)", padding: "48px 24px", textAlign: "center" }}>
            <p style={{ fontFamily: "var(--serif-display)", fontSize: "18px", color: "var(--ink-70)", marginBottom: "12px" }}>
              No authors found matching your criteria.
            </p>
            <button
              onClick={() => {
                setSearch("");
                setSelectedLetter("ALL");
                setSelectedCategory("ALL");
              }}
              className="btn btn-primary btn-sm"
            >
              Clear All Filters
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {filteredAuthors.map((author) => {
              const isExpanded = !!expandedAuthors[author.name];
              const scholarUrl = getGoogleScholarAuthorUrl(author.name);
              const profileUrl = getGoogleScholarAuthorProfileUrl(author.name);
              const journalScholarUrl = getGoogleScholarJournalAuthorUrl(author.name);

              return (
                <div
                  key={author.name}
                  id={`author-${author.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  style={{
                    background: "white",
                    border: "1px solid var(--ink-15)",
                    transition: "box-shadow 0.2s, border-color 0.2s",
                  }}
                >
                  {/* Card Header Row */}
                  <div
                    style={{
                      padding: "20px 24px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "20px",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    {/* Left: Avatar + Name + Affiliation */}
                    <div style={{ display: "flex", gap: "16px", alignItems: "center", flex: "1 1 320px" }}>
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "4px",
                          background: "var(--ink)",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: "var(--serif-display)",
                          fontSize: "18px",
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {author.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                          <h2
                            style={{
                              fontFamily: "var(--serif-display)",
                              fontSize: "20px",
                              fontWeight: 700,
                              color: "var(--ink)",
                              margin: 0,
                              lineHeight: 1.2,
                            }}
                          >
                            {author.name}
                          </h2>
                          <span
                            style={{
                              fontFamily: "var(--mono)",
                              fontSize: "10px",
                              padding: "2px 8px",
                              background: "var(--ink-07)",
                              color: "var(--ink-70)",
                              borderRadius: "2px",
                              letterSpacing: "0.05em",
                            }}
                          >
                            {author.publicationCount} {author.publicationCount === 1 ? "Publication" : "Publications"}
                          </span>
                        </div>

                        {author.affiliationsList.length > 0 && (
                          <p
                            style={{
                              fontFamily: "var(--serif-body)",
                              fontStyle: "italic",
                              fontSize: "13px",
                              color: "var(--ink-70)",
                              margin: "4px 0 0",
                              lineHeight: 1.4,
                            }}
                          >
                            {author.affiliationsList[0]}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Right: Google Scholar Mechanism Action Buttons */}
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                      <a
                        href={scholarUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm"
                        title="Search all publications by this author on Google Scholar"
                        style={{
                          fontFamily: "var(--mono)",
                          fontSize: "10px",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          background: "#F8FAFC",
                          borderColor: "#CBD5E1",
                          color: "#1E293B",
                        }}
                      >
                        <span style={{ fontSize: "12px" }}>🎓</span>
                        Scholar Search ↗
                      </a>

                      <a
                        href={profileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm"
                        title="Search author profile and citation metrics on Google Scholar"
                        style={{
                          fontFamily: "var(--mono)",
                          fontSize: "10px",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          background: "#F8FAFC",
                          borderColor: "#CBD5E1",
                          color: "#1E293B",
                        }}
                      >
                        <span>👤</span>
                        Scholar Profile ↗
                      </a>

                      <a
                        href={journalScholarUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm"
                        title="Search papers authored in ScienceXplorer on Google Scholar"
                        style={{
                          fontFamily: "var(--mono)",
                          fontSize: "10px",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          background: "white",
                          borderColor: "var(--ink-15)",
                          color: "var(--ink-70)",
                        }}
                      >
                        <span>🔍</span>
                        In ScienceXplorer ↗
                      </a>

                      <button
                        onClick={() => copyAuthorBibtex(author)}
                        className="btn btn-ghost btn-sm"
                        title="Copy BibTeX citation for all articles by this author"
                        style={{
                          fontFamily: "var(--mono)",
                          fontSize: "10px",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                        }}
                      >
                        Copy BibTeX
                      </button>

                      <button
                        onClick={() => toggleExpand(author.name)}
                        className="btn btn-sm"
                        style={{
                          fontFamily: "var(--mono)",
                          fontSize: "10px",
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          background: isExpanded ? "var(--ink)" : "white",
                          color: isExpanded ? "white" : "var(--ink)",
                          borderColor: "var(--ink)",
                        }}
                      >
                        {isExpanded ? "Hide Papers ▲" : `View Papers (${author.publicationCount}) ▼`}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Papers Sub-Panel */}
                  {isExpanded && (
                    <div
                      style={{
                        borderTop: "1px solid var(--ink-15)",
                        background: "#FDFDFD",
                        padding: "20px 24px",
                      }}
                    >
                      <div
                        style={{
                          fontFamily: "var(--mono)",
                          fontSize: "10px",
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          color: "var(--ink-40)",
                          marginBottom: "14px",
                        }}
                      >
                        Published Articles ({author.articles.length})
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                        {author.articles.map((art) => {
                          const artScholarUrl = getGoogleScholarArticleUrl(art.title, art.doi);
                          const year = art.published_at ? new Date(art.published_at).getFullYear() : (art.pub_year || "");

                          return (
                            <div
                              key={art.id}
                              style={{
                                background: "white",
                                border: "1px solid var(--ink-15)",
                                padding: "16px 20px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
                                <button
                                  onClick={() => navigate("article", art.id)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    textAlign: "left",
                                    fontFamily: "var(--serif-display)",
                                    fontSize: "16px",
                                    fontWeight: 700,
                                    color: "var(--ink)",
                                    cursor: "pointer",
                                    lineHeight: 1.35,
                                    flex: "1 1 300px",
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.color = "var(--scarlet)")}
                                  onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink)")}
                                >
                                  {art.title}
                                </button>

                                <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                                  <span
                                    style={{
                                      fontFamily: "var(--mono)",
                                      fontSize: "9px",
                                      padding: "2px 6px",
                                      background: "var(--ink-07)",
                                      color: "var(--ink-70)",
                                      letterSpacing: "0.06em",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {art.authorRole}
                                  </span>
                                  {art.category && (
                                    <span
                                      style={{
                                        fontFamily: "var(--mono)",
                                        fontSize: "9px",
                                        padding: "2px 6px",
                                        background: "rgba(37,99,235,0.08)",
                                        color: "#2563EB",
                                        letterSpacing: "0.06em",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      {art.category}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Metadata Line */}
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "16px",
                                  alignItems: "center",
                                  fontFamily: "var(--mono)",
                                  fontSize: "10px",
                                  color: "var(--ink-40)",
                                }}
                              >
                                <span>Vol. {art.volume || "1"}, No. {art.issue || "1"} ({year || "2026"})</span>
                                {art.doi && (
                                  <a
                                    href={art.doi}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: "var(--scarlet)", textDecoration: "underline" }}
                                  >
                                    DOI: {String(art.doi).replace(/^https?:\/\/(?:dx\.)?doi\.org\//i, "")}
                                  </a>
                                )}
                              </div>

                              {/* Paper Action Buttons */}
                              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "4px" }}>
                                <button
                                  onClick={() => navigate("article", art.id)}
                                  className="btn btn-sm"
                                  style={{
                                    fontFamily: "var(--mono)",
                                    fontSize: "10px",
                                    letterSpacing: "0.06em",
                                    textTransform: "uppercase",
                                    padding: "6px 14px",
                                  }}
                                >
                                  Read Full Abstract
                                </button>

                                {art.pdf_url && (
                                  <a
                                    href={art.pdf_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-primary btn-sm"
                                    style={{
                                      fontFamily: "var(--mono)",
                                      fontSize: "10px",
                                      letterSpacing: "0.06em",
                                      textTransform: "uppercase",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "5px",
                                      padding: "6px 14px",
                                    }}
                                  >
                                    Download PDF ↗
                                  </a>
                                )}

                                <a
                                  href={artScholarUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-ghost btn-sm"
                                  style={{
                                    fontFamily: "var(--mono)",
                                    fontSize: "10px",
                                    letterSpacing: "0.06em",
                                    textTransform: "uppercase",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "5px",
                                  }}
                                >
                                  <span>🎓</span>
                                  Query on Google Scholar ↗
                                </a>

                                <button
                                  onClick={() => {
                                    setScholarModalData(art);
                                  }}
                                  className="btn btn-ghost btn-sm"
                                  style={{
                                    fontFamily: "var(--mono)",
                                    fontSize: "10px",
                                    letterSpacing: "0.06em",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Inspect Meta Tags
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Google Scholar Indexing Mechanism & Guide for Authors */}
        <div
          style={{
            marginTop: "56px",
            background: "white",
            border: "1px solid var(--ink-15)",
            padding: "36px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
            <span style={{ fontSize: "20px" }}>🎓</span>
            <div
              style={{
                fontFamily: "var(--mono)",
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "var(--scarlet)",
              }}
            >
              Google Scholar Indexing Mechanism
            </div>
          </div>
          <h2
            style={{
              fontFamily: "var(--serif-display)",
              fontSize: "24px",
              fontWeight: 700,
              marginBottom: "14px",
              color: "var(--ink)",
            }}
          >
            How ScienceXplorer Indexes Authors & Articles
          </h2>
          <p
            style={{
              fontSize: "15px",
              lineHeight: 1.7,
              color: "var(--ink-70)",
              marginBottom: "24px",
              maxWidth: "800px",
            }}
          >
            ScienceXplorer implements Google Scholar's official <em>Inclusion Guidelines for Webmasters and Publishers</em>.
            Every manuscript page delivers complete machine-readable Highwire Press and Dublin Core meta tags,
            unrestricted crawlable full-text PDFs, and XML sitemaps.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "20px",
              marginBottom: "32px",
            }}
          >
            <div style={{ border: "1px solid var(--ink-07)", background: "var(--ink-07)", padding: "18px" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: "11px", fontWeight: 700, marginBottom: "6px", color: "var(--ink)" }}>
                1. Highwire Press Tags
              </div>
              <p style={{ fontSize: "13px", lineHeight: 1.5, color: "var(--ink-70)" }}>
                HTML headers deliver <code>citation_title</code>, <code>citation_author</code>, <code>citation_pdf_url</code>, and <code>citation_doi</code> to academic indexers.
              </p>
            </div>

            <div style={{ border: "1px solid var(--ink-07)", background: "var(--ink-07)", padding: "18px" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: "11px", fontWeight: 700, marginBottom: "6px", color: "var(--ink)" }}>
                2. Automated Author Linking
              </div>
              <p style={{ fontSize: "13px", lineHeight: 1.5, color: "var(--ink-70)" }}>
                Every co-author is parsed individually without superscript numbers or corrupted characters, matching Google Scholar's standard format.
              </p>
            </div>

            <div style={{ border: "1px solid var(--ink-07)", background: "var(--ink-07)", padding: "18px" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: "11px", fontWeight: 700, marginBottom: "6px", color: "var(--ink)" }}>
                3. Open-Access Sitemaps
              </div>
              <p style={{ fontSize: "13px", lineHeight: 1.5, color: "var(--ink-70)" }}>
                Dedicated <code>/sitemap.xml</code> and <code>/sitemap-scholar.xml</code> provide direct links for Googlebot and academic crawlers.
              </p>
            </div>

            <div style={{ border: "1px solid var(--ink-07)", background: "var(--ink-07)", padding: "18px" }}>
              <div style={{ fontFamily: "var(--mono)", fontSize: "11px", fontWeight: 700, marginBottom: "6px", color: "var(--ink)" }}>
                4. Direct PDF Hosting
              </div>
              <p style={{ fontSize: "13px", lineHeight: 1.5, color: "var(--ink-70)" }}>
                Full-text PDFs are served directly over HTTPS with static canonical endpoints, allowing automated text indexing and citation linking.
              </p>
            </div>
          </div>

          <div
            style={{
              padding: "20px",
              background: "#F0FDF4",
              border: "1px solid #BBF7D0",
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: "11px", fontWeight: 700, color: "#166534", marginBottom: "4px" }}>
                Authors: Link Your ScienceXplorer Paper to Your Google Scholar Profile
              </div>
              <p style={{ fontSize: "13px", color: "#15803D", margin: 0 }}>
                1. Go to your Google Scholar Citations profile → 2. Click "+" (Add articles) → 3. Search your article title or DOI.
              </p>
            </div>
            <a
              href="https://scholar.google.com/citations?view_op=new_profile"
              target="_blank"
              rel="noreferrer"
              className="btn btn-sm"
              style={{
                background: "#166534",
                color: "white",
                borderColor: "#166534",
                fontFamily: "var(--mono)",
                fontSize: "10px",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              Open Google Scholar Citations ↗
            </a>
          </div>
        </div>

        {/* Modal: Inspect Live Highwire Press Meta Tags */}
        {scholarModalData && (
          <div
            onClick={() => setScholarModalData(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.65)",
              zIndex: 300,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="animate-up"
              style={{
                background: "white",
                width: "100%",
                maxWidth: "760px",
                maxHeight: "85vh",
                overflowY: "auto",
                padding: "36px",
                position: "relative",
              }}
            >
              <button
                onClick={() => setScholarModalData(null)}
                style={{
                  position: "absolute",
                  top: "16px",
                  right: "20px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "24px",
                  color: "var(--ink-40)",
                }}
              >
                ×
              </button>

              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <span style={{ fontSize: "16px" }}>🎓</span>
                <span style={{ fontFamily: "var(--mono)", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--scarlet)" }}>
                  Google Scholar Metadata Inspector
                </span>
              </div>

              <h3 style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 700, marginBottom: "16px", lineHeight: 1.3 }}>
                {scholarModalData.title}
              </h3>

              <p style={{ fontSize: "13px", color: "var(--ink-70)", marginBottom: "16px" }}>
                The following Highwire Press and Dublin Core HTML meta tags are embedded directly in the page header for Google Scholar crawlers:
              </p>

              <div
                style={{
                  background: "#0F172A",
                  color: "#E2E8F0",
                  padding: "16px",
                  fontFamily: "var(--mono)",
                  fontSize: "11px",
                  lineHeight: 1.7,
                  overflowX: "auto",
                  marginBottom: "20px",
                  borderRadius: "2px",
                }}
              >
                {generateHighwireTags(scholarModalData).map((t, idx) => (
                  <div key={idx}>
                    &lt;meta name="{t.name}" content="{t.content}" /&gt;
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  onClick={() => {
                    const tagString = generateHighwireTags(scholarModalData)
                      .map((t) => `<meta name="${t.name}" content="${t.content}" />`)
                      .join("\n");
                    navigator.clipboard.writeText(tagString).then(() => {
                      showToast("Copied HTML meta tags to clipboard!");
                    });
                  }}
                  className="btn btn-primary btn-sm"
                >
                  Copy All Meta Tags
                </button>
                <button onClick={() => setScholarModalData(null)} className="btn btn-ghost btn-sm">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast Notification */}
        {toastMsg && (
          <div
            className="animate-up"
            style={{
              position: "fixed",
              bottom: "28px",
              right: "28px",
              background: "var(--ink)",
              color: "white",
              padding: "12px 20px",
              fontFamily: "var(--mono)",
              fontSize: "11px",
              letterSpacing: "0.06em",
              zIndex: 400,
              boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
            }}
          >
            {toastMsg}
          </div>
        )}
      </div>
    </div>
  );
}
